import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch } from '../../lib/api'
import { Pagination, DEFAULT_PAGE_SIZE_OPTIONS } from '../../components/Pagination'
import { SortableTh, toggleSort, type SortState } from '../../components/mcp/analytics/SortableTh'
import { useTableState } from './useTableState'
import {
  StackedTimeSeries,
  type SeriesPoint,
} from '../../components/mcp/analytics/StackedTimeSeries'
import {
  ChannelBadge,
  DataCell,
  InvocationModeBadge,
  InvocationTypeBadge,
  ResponseStatusBadge,
} from '../../components/mcp/analytics/badges'
import { INVOCATION_TYPES } from '../../lib/mcpAnalytics/invocationTypes'
import { descriptionText } from '../../lib/mcpAnalytics/description'
import { formatBucketFullLabel, formatDateTime } from '../../lib/formatDateTime'
import { TabState, TabNoDataYet } from './TabStates'
import { MCP_ANALYTICS_BASE, buildFilterParams, withInvocationTypes, withTableParams } from './api'
import {
  resolveChartPeriod,
  resolvePeriod,
  viewerTimeZone,
  type AnalyticsFilterState,
  type Granularity,
} from './useAnalyticsFilters'
import type {
  McpAnalyticsInvocationListResponse,
  McpAnalyticsSeriesResponse,
} from '../../lib/types'

/* ─── Tool Invocations (MS-701) ───

   A stacked column chart over a continuous time axis, and below it the paginated
   invocation table. The two are cross-filtered: clicking a legend entry isolates
   an invocation type, clicking a bar narrows the table to that bucket.

   Both narrowings are sent to the **API**, not applied to the loaded page. Filtering
   client-side would leave `totalCount`/`totalPages` describing a different set from
   the rows on screen — a pager that pages through nothing.

   The chart's window is narrower than the table's at day granularity (a trailing
   ~3 months, MS-701 AC2). That asymmetry is intended, which is why a bar click
   sends the bucket's own explicit bounds rather than assuming the two share a
   window. */

const GRANULARITIES: Granularity[] = ['day', 'month', 'quarter', 'year']

const COLUMNS = [
  { key: 'timestamp', labelKey: 'timestamp', descendingFirst: true },
  { key: 'user', labelKey: 'user', descendingFirst: false },
  { key: 'channel', labelKey: 'channel', descendingFirst: false },
  { key: 'type', labelKey: 'type', descendingFirst: false },
  { key: 'status', labelKey: 'status', descendingFirst: false },
  { key: 'callType', labelKey: 'callType', descendingFirst: false },
  { key: 'description', labelKey: 'description', descendingFirst: false },
] as const

export const DEFAULT_SORT: SortState = { col: 'timestamp', dir: 'desc' }
/** The pager's first size option, matching `mcp_analytics_default_page_size`. */
const DEFAULT_PAGE_SIZE = 10

/** What a restored table state may contain, derived from what this tab actually
 *  renders so the two cannot drift apart. */
const ALLOWED = {
  sortCols: COLUMNS.map(c => c.key),
  pageSizes: DEFAULT_PAGE_SIZE_OPTIONS,
}

/** One bucket's [start, end) as the table's window. Derived from the spine — the
 *  next bucket's start is this one's end — so it cannot drift from the server's
 *  own truncation, and the last bucket ends at the chart window's end. */
export function bucketBounds(
  buckets: string[],
  bucket: string,
  windowEnd: string,
): { from: string; to: string } | null {
  const index = buckets.indexOf(bucket)
  if (index < 0) return null
  const next = buckets[index + 1]
  const end = next
    ? new Date(new Date(next).getTime() - 1).toISOString()
    : windowEnd
  return { from: bucket, to: end }
}

export interface InvocationsTabProps {
  connectionKey: string
  filters: AnalyticsFilterState
  filtersKey: unknown[]
  /** Bumped by the refresh control and by tab activation to force a refetch. */
  reloadToken: number
}

export function InvocationsTab({
  connectionKey,
  filters,
  filtersKey,
  reloadToken,
}: InvocationsTabProps) {
  const { t, i18n } = useTranslation()
  const apiFetch = useApiFetch()
  const timeZone = viewerTimeZone()
  // Instants render in the viewer's zone and the interface language: changing the
  // language changes the month's name, never the moment it names.
  const dateOptions = { timeZone, locale: i18n.language }

  const [granularity, setGranularity] = useState<Granularity>('day')
  const [isolatedTypeKey, setIsolatedTypeKey] = useState<string | null>(null)
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null)
  // Sort, page and page size survive a tab switch (the tab unmounts), while a
  // filter change still resets to page 1 below. `reloadToken` is deliberately not
  // in that dependency list: a refresh or a tab reload preserves the page.
  const { sort, page, pageSize, setSort, setPage, setPageSize, resetPage } = useTableState(
    connectionKey,
    'invocations',
    { sort: DEFAULT_SORT, page: 1, pageSize: DEFAULT_PAGE_SIZE },
    ALLOWED,
  )

  const filtersSignature = JSON.stringify(filtersKey)
  useEffect(() => {
    resetPage()
    setSelectedBucket(null)
  }, [filtersSignature, resetPage])

  const chartWindow = useMemo(
    () => resolveChartPeriod(filters, granularity),
    [filters, granularity],
  )
  const tableWindow = useMemo(() => resolvePeriod(filters), [filters])

  const isolatedStoredValues = useMemo(
    () => INVOCATION_TYPES.find(type => type.key === isolatedTypeKey)?.storedValues ?? [],
    [isolatedTypeKey],
  )

  const seriesQuery = useQuery({
    queryKey: ['mcp-analytics', 'series', filtersKey, granularity, isolatedTypeKey, reloadToken],
    placeholderData: (prev) => prev,
    queryFn: () => {
      const params = withInvocationTypes(
        buildFilterParams(filters, chartWindow),
        isolatedStoredValues,
      )
      params.set('granularity', granularity)
      params.set('tz', timeZone)
      return apiFetch<McpAnalyticsSeriesResponse>(
        `${MCP_ANALYTICS_BASE}/invocations/series?${params.toString()}`,
      )
    },
  })

  const buckets = seriesQuery.data?.buckets ?? []
  const bucketWindow = selectedBucket
    ? bucketBounds(buckets, selectedBucket, chartWindow.to)
    : null

  const listQuery = useQuery({
    queryKey: [
      'mcp-analytics',
      'invocations',
      filtersKey,
      sort.col,
      sort.dir,
      page,
      pageSize,
      isolatedTypeKey,
      selectedBucket,
      reloadToken,
    ],
    placeholderData: (prev) => prev,
    queryFn: () => {
      const params = withTableParams(
        withInvocationTypes(
          buildFilterParams(filters, bucketWindow ?? tableWindow),
          isolatedStoredValues,
        ),
        { sortCol: sort.col, sortDir: sort.dir, page, pageSize },
      )
      return apiFetch<McpAnalyticsInvocationListResponse>(
        `${MCP_ANALYTICS_BASE}/invocations?${params.toString()}`,
      )
    },
  })

  const handleSort = useCallback(
    (colKey: string) => {
      const column = COLUMNS.find(c => c.key === colKey)
      setSort(toggleSort(sort, colKey, column?.descendingFirst ?? false))
    },
    [sort, setSort],
  )

  const items = listQuery.data?.items ?? []
  const points: SeriesPoint[] = seriesQuery.data?.points ?? []
  const isFiltered = isolatedTypeKey !== null || selectedBucket !== null
  const hasAnyData = (listQuery.data?.totalCount ?? 0) > 0 || points.length > 0

  const filterCriteria = [
    isolatedTypeKey
      ? INVOCATION_TYPES.find(type => type.key === isolatedTypeKey)?.label
      : null,
    selectedBucket ? formatBucketFullLabel(selectedBucket, granularity, dateOptions) : null,
  ]
    .filter(Boolean)
    .join(' · ')

  function clearCrossFilter() {
    setIsolatedTypeKey(null)
    setSelectedBucket(null)
    resetPage()
  }

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3>{t('mcpAnalytics.detail.invocations.title')}</h3>
          <p>{t('mcpAnalytics.detail.invocations.subtitle')}</p>
        </div>
      </div>
      <div className="card-body">
        <TabState
          isLoading={seriesQuery.isLoading}
          isError={seriesQuery.isError}
          error={seriesQuery.error}
          onRetry={() => void seriesQuery.refetch()}
        >
          <div className="ts-toolbar">
            <div className="legend-row">
              <span className="legend-title">
                {t('mcpAnalytics.detail.invocations.legendTitle')}
              </span>
              {INVOCATION_TYPES.map(type => (
                <button
                  key={type.key}
                  type="button"
                  className={[
                    'legend-item clickable',
                    isolatedTypeKey && isolatedTypeKey !== type.key ? 'dim' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={isolatedTypeKey === type.key}
                  onClick={() => {
                    setIsolatedTypeKey(prev => (prev === type.key ? null : type.key))
                    resetPage()
                  }}
                >
                  <span className="legend-swatch" style={{ background: type.colorVar }} />
                  {type.label}
                </button>
              ))}
            </div>

            <div
              className="ops-tree-seg"
              role="group"
              aria-label={t('mcpAnalytics.detail.invocations.granularityAria')}
            >
              {GRANULARITIES.map(value => (
                <button
                  key={value}
                  type="button"
                  className={granularity === value ? 'on' : undefined}
                  aria-pressed={granularity === value}
                  onClick={() => {
                    setGranularity(value)
                    setSelectedBucket(null)
                  }}
                >
                  {t(`mcpAnalytics.detail.invocations.granularity.${value}` as const)}
                </button>
              ))}
            </div>
          </div>

          <div className="ts-chart-area">
            <div className="ts-ytitle">{t('mcpAnalytics.detail.invocations.yAxis')}</div>
            <StackedTimeSeries
              buckets={buckets}
              points={points}
              granularity={granularity}
              timeZone={timeZone}
              isolatedTypeKey={isolatedTypeKey}
              selectedBucket={selectedBucket}
              onSelectBucket={bucket => {
                setSelectedBucket(bucket)
                resetPage()
              }}
              emptyLabel={t('mcpAnalytics.detail.invocations.chartEmpty')}
            />
          </div>
          <div className="ts-xtitle">
            {t(`mcpAnalytics.detail.invocations.xAxis.${granularity}` as const)}
          </div>

          {isFiltered && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, margin: '14px 0 0' }}>
              <span className="band-pill">
                {t('mcpAnalytics.detail.invocations.filtered', { criteria: filterCriteria })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 11, padding: '3px 10px' }}
                onClick={clearCrossFilter}
              >
                {t('mcpAnalytics.detail.invocations.clearFilter')}
              </button>
            </div>
          )}
        </TabState>

        <TabState
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
        >
          <div style={{ marginTop: 18 }}>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalCount={listQuery.data?.totalCount ?? 0}
              totalPages={listQuery.data?.totalPages ?? 0}
              itemsOnPage={items.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onResetSort={() => setSort(DEFAULT_SORT)}
              isSortDefault={sort.col === DEFAULT_SORT.col && sort.dir === DEFAULT_SORT.dir}
              renderCount={({ from, to, total }) =>
                t('mcpAnalytics.detail.invocations.pager.showing', { from, to, total })
              }
              emptyLabel={t('mcpAnalytics.detail.invocations.pager.empty')}
            />

            <table className="data-table">
              <thead>
                <tr>
                  {COLUMNS.map(column => (
                    <SortableTh
                      key={column.key}
                      colKey={column.key}
                      label={t(`mcpAnalytics.detail.invocations.columns.${column.labelKey}` as const)}
                      sort={sort}
                      onSort={handleSort}
                      defaultsToDescending={column.descendingFirst}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.eventId}>
                    <td>{formatDateTime(item.timestamp, dateOptions)}</td>
                    <td>{item.name}</td>
                    <td>
                      <ChannelBadge channel={item.channel} agentName={item.agentName} />
                    </td>
                    <td>
                      <InvocationTypeBadge invocationType={item.invocationType} />
                    </td>
                    <td>
                      <ResponseStatusBadge status={item.responseStatus} />
                    </td>
                    <td>
                      <InvocationModeBadge mode={item.invocationMode} />
                    </td>
                    <td>
                      <DataCell value={descriptionText(item)} />
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} style={{ color: 'var(--text-tertiary)', padding: 14 }}>
                      {hasAnyData || isFiltered ? (
                        t('mcpAnalytics.detail.invocations.tableEmpty')
                      ) : (
                        <TabNoDataYet />
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabState>
      </div>
    </div>
  )
}
